import { useMemo } from 'react';

import { RooftopsCreateDto, RooftopUpdateDto } from '@libs/dto';
import { useLoading, useSnackbar, useConfirmation } from '@carcareers/contexts';
import { isError, getErrors, getErrorsAsString } from '@carcareers/utils';
import { rooftopApi } from '@carcareers/store';
import { ApiError, ApiResponse } from '@libs/types';
import { Rooftop } from '@libs/models';

interface RooftopCallbacks {
  onSuccess?: () => void;
}

export const useRooftop = () => {
  const { setLoading, setLabel } = useLoading();
  const { toggleSnackbar } = useSnackbar();
  const { confirm } = useConfirmation();
  const [rooftopCreate] = rooftopApi.useRooftopCreateMutation();
  const [rooftopUpdate] = rooftopApi.useRooftopUpdateMutation();
  const [rooftopDelete] = rooftopApi.useRooftopDeleteMutation();

  return useMemo(
    () => ({
      createRooftop: async (
        rooftop?: RooftopsCreateDto,
        callbacks?: RooftopCallbacks,
      ): Promise<ApiResponse<Rooftop>> => {
        if (!rooftop) {
          toggleSnackbar('warning', 'Rooftop data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Creating rooftop...');
        setLoading(true);

        const response = await rooftopCreate(rooftop);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Rooftop created');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      updateRooftop: async (
        rooftop?: RooftopUpdateDto,
        callbacks?: RooftopCallbacks,
      ): Promise<ApiResponse<Rooftop>> => {
        if (!rooftop) {
          toggleSnackbar('warning', 'Rooftop data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating rooftop...');
        setLoading(true);
        
        const response = await rooftopUpdate(rooftop);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Rooftop updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      deleteRooftop: async (id?: number, callbacks?: RooftopCallbacks) => {
        if (!id) {
          toggleSnackbar('warning', 'Rooftop ID is empty.');
          return;
        }

        const confirmed = await confirm({
          title: 'Delete Rooftop',
          message: 'Are you sure you want to delete this rooftop?',
        });

        if (!confirmed) return;

        setLabel('Deleting rooftop...');
        setLoading(true);
        const response = await rooftopDelete(id);

        if (isError(response)) {
          toggleSnackbar('error', getErrorsAsString(response));
        } else {
          toggleSnackbar('success', 'Rooftop deleted');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        }

        setLoading(false);
        return response;
      },
    }),
    [toggleSnackbar, confirm, setLoading, setLabel, rooftopCreate, rooftopUpdate, rooftopDelete],
  );
};
